<template>
  <div>
    <!-- stepper  -->
    <Stepper
      :steps="videos.length"
      :current="currentStep"
      :onSelectStep="onSelectStep"
      :tooltipData="tooltipData"
      :isNextStepPassed="isNextStepPassed"
      :isLastStepCompleted="isLastVideoCompleted"
      v-show="!showThankYou"
    />

    <!-- intro || lesson video || lesson quiz -->
    <div v-if="video">
      <PretrainingThankYou v-if="showThankYou" />
      <PretrainingIntroVideo
        v-else-if="isIntroVideo"
        :video="video"
        :topics="introVideoTopics"
        :passed="isIntroVideoPassed"
        :onPauseVideo="onPauseVideo"
        :onEndVideo="onEndVideo"
        :onResetVideo="onResetVideo"
        @next="setStep"
      />
      <PretrainingLessonVideo
        v-else-if="!quiz && !isIntroVideo"
        :video="video"
        :checklists="checklists"
        v-on:checkValueList="checkValueList"
        :checkedListItems="checkedListItems"
        :onPauseVideo="onPauseVideo"
        :onEndVideo="onEndVideo"
        :onResetVideo="onResetVideo"
        :isLast="isLastVideo"
        :hasTraining="hasTraining"
        :quiz="quiz"
        :product="product"
        :customerGroup="customerGroup"
        @fetchQuiz="fetchQuiz"
      />
      <PretrainingLessonQuiz
        v-else
        :quiz="quiz"
        :title="`Quiz ${video.order - 1}: ${video.title}`"
        :isLast="isLastVideo"
        @success="onQuizSuccess"
        @fail="onQuizFail"
      />
    </div>
    <div v-else class="text-lg font-semibold text-center">
      {{ $t('preTraining.noPretrainingAvailable') }}
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';

import { axiosClient } from '../../api';
import PretrainingLessonVideo from './PretrainingLessonVideo';
import PretrainingLessonQuiz from './PretrainingLessonQuiz';
import PretrainingIntroVideo from './PreTrainingIntroVideo';
import PretrainingThankYou from '@/components/customer/PretrainingThankYou';
import Stepper from './Stepper';

export default {
  components: {
    PretrainingIntroVideo,
    PretrainingLessonVideo,
    PretrainingLessonQuiz,
    PretrainingThankYou,
    Stepper,
  },
  data() {
    return {
      selectedStep: null,
      showThankYou: false,
      customerGroup: {},
    };
  },
  computed: mapState({
    product: state => state.customer.product ?? {},
    videos: state => state.customer.videos,
    checklists: state => state.customer.checklists,
    checkedListItems: state => state.customer.checkedListItems,
    quiz: state => state.customer.quiz,
    user: state => state.auth.user,
    video() {
      let video;
      // check 1 - start at intro video if is yet to be completed and following videos have not been passed
      video = this.videos.find(
        v =>
          v.is_intro_video &&
          (!v.progress || (v.progress && !v.progress.complete)) &&
          !this.isNextStepPassed,
      );
      if (video) return video;
      // check 2 - exact order and passed video or intro
      if (this.selectedStep) {
        video = this.videos.find(
          v =>
            v.order === this.selectedStep &&
            (v.passed || this.isNextStepPassed || v.is_intro_video),
        );
        if (video) return video;
      }
      // check 3 - no specific order, exclude intro video and select first non passed video
      // and where following video is yet to be passed
      video = this.videos.find(v => !v.passed && !v.is_intro_video && !this.isNextStepPassed);
      if (video) return video;

      // check 4 - all videos are passed and there's not other selection
      return this.videos[this.videos.length - 1];
    },
    lastVideo() {
      return this.videos[this.videos.length - 1];
    },
    introVideoTopics() {
      return this.videos.filter(video => !video.is_intro_video).map(p => p.title);
    },
    isIntroVideo() {
      return !!this.video.is_intro_video;
    },
    isIntroVideoPassed() {
      // way around checking if intro video is completed since we don't set 'passed' prop for this video in the db
      const videoNextToIntro = this.videos[2];
      return videoNextToIntro ? !!videoNextToIntro.passed : false;
    },
    tooltipData() {
      return this.videos.map(v => v.title);
    },
    currentStep() {
      const video = this.videos.find(
        v =>
          (v.is_intro_video && (!v.progress || (v.progress && !v.progress.complete))) ||
          (!v.is_intro_video && !v.passed),
      );
      return video ? video.order : null;
    },
    isNextStepPassed() {
      // necessary check in order to keep step check when retaking a quiz
      // as videos.passed field is reseted on quiz fail and the steppers logic is based on it
      return (
        this.currentStep && this.videos.some(v => v.order === this.currentStep + 1 && !!v.passed)
      );
    },
    isLastVideo() {
      const index = this.videos.findIndex(v => v.id === this.video.id);
      return index === this.videos.length - 1;
    },
    hasTraining() {
      return this.product && this.product.has_training ? true : false;
    },
    isLastVideoCompleted() {
      // since the last video has not quiz we need to find a way to check the step
      return this.lastVideo && this.lastVideo.progress ? !!this.lastVideo.progress.complete : false;
    },
    lastStep() {
      return this.videos[this.videos.length - 1].order;
    },
  }),
  methods: {
    setStep() {
      const nextOrder = this.video.order + 1;
      const video = this.videos.find(v => !v.is_intro_video && v.order === nextOrder);
      this.selectedStep = video ? video.order : null;
    },
    onSelectStep(order) {
      this.selectedStep = order;
      this.$store.commit('customer/setQuiz', { quiz: null });
      this.$store.commit('customer/setCheckLists', { checklists: [] });
      this.fetchChecklists(this.video.id);
      this.fetchCheckedListItems(this.video.id);
    },
    checkValueList(event) {
      const value = event.target.value;
      if (event.target.checked) {
        this.saveCheckList(value);
      } else {
        this.deleteCheckList(value);
      }
    },
    async fetchCustomerGroups() {
      if (this.product.group) {
        const res = await axiosClient.get(`training/customer-groups/${this.product.group.id}`);
        this.customerGroup = res.data ? res.data : {};
      }
    },
    async fetchChecklists(videoId) {
      this.$store.dispatch('customer/getCheckLists', { videoId, langCode: this.$language.code });
    },
    async fetchCheckedListItems(videoId) {
      this.$store.dispatch('customer/getCheckedListsItems', { videoId });
    },
    async saveCheckList(checklist_id) {
      try {
        await axiosClient.post(`videos/${this.video.id}/checklists`, {
          checklist_id: checklist_id,
        });
        this.$store.dispatch('customer/addCheckedListsItems', { checklist_id });
      } catch (error) {
        console.error({ ...error });
      }
    },
    async deleteCheckList(checklist_id) {
      try {
        await axiosClient.delete(`videos/${this.video.id}/checklists/${checklist_id}`);
        await this.fetchCheckedListItems(this.video.id);
      } catch (error) {
        console.error({ ...error });
      }
    },
    async fetchQuiz(videoId, langCode) {
      if (this.isLastVideo) {
        return this.unlockTrainings();
      }
      this.$store.dispatch('customer/getQuiz', { videoId, langCode });
    },
    async unlockTrainings() {
      if (this.user && !this.user.unlocked_trainings) {
        await this.$store.dispatch('auth/unlockTrainings');
      }
      if (
        Object.keys(this.customerGroup).length === 0 &&
        this.product.requires_rep_approval &&
        this.product.zendesk_value &&
        this.product.group
      ) {
        await axiosClient.post(`training/create-ticket/`, {
          group_id: this.product.group.id,
          zendesk_machine_value: this.product.zendesk_value,
        });
        this.showThankYou = true;
        return;
      }
      return this.$router.push('/schedules');
    },
    onQuizSuccess() {
      this.setStep();
      this.$store.commit('customer/setQuiz', { quiz: null });
      this.$store.commit('customer/updateVideos', { video: { ...this.video, passed: true } });
      this.$store.commit('customer/setCheckLists', { checklists: [] });
      this.fetchChecklists(this.video.id);
      this.fetchCheckedListItems(this.video.id);
    },
    onQuizFail() {
      this.$store.commit('customer/setQuiz', { quiz: null });
    },
    onPauseVideo(currentTime, duration) {
      if (Math.round(currentTime) < Math.round(duration)) {
        this.$store.dispatch('customer/saveVideoProgress', {
          videoId: this.video.id,
          time: currentTime,
        });
      }
    },
    onEndVideo(currentTime) {
      this.$store.dispatch('customer/saveVideoProgress', {
        videoId: this.video.id,
        time: currentTime,
        complete: 1,
      });
    },
    onResetVideo() {
      this.$store.dispatch('customer/saveVideoProgress', {
        videoId: this.video.id,
        time: 0,
        complete: this.video.is_intro_video ? 1 : 0, // resetting this field on intro video ruins steppers' logic
      });
    },
  },
  async mounted() {
    // fetch checklists
    if (this.video && this.video.id) {
      await this.fetchCustomerGroups();
      await this.fetchChecklists(this.video.id);
      await this.fetchCheckedListItems(this.video.id);
    }
  },
};
</script>
